/**
 * PlanStatusSelect
 *
 * Per-plan stage sub-status dropdown that lives directly under the progress
 * pipeline. Mirrors the legacy plan-stage-pipeline.php "Status" <select>
 * (lines 264-285): options come from `stages_sub` for the plan's currently
 * selected stage with `active=1`, plus the legacy implicit `None=0` option.
 * On change, PUTs `{ stageSubId }` to /plans/{id}, which the backend routes
 * through the existing `runStatusTrigger` pipeline.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@/components/ui';
import * as plansSvc from '@/services/plans.service';

interface PlanStatusSelectProps {
  planId: number;
  patientId?: number;
  /** When true, omits the built-in "Status" label above the select. */
  hideLabel?: boolean;
}

export function PlanStatusSelect({ planId, patientId, hideLabel }: PlanStatusSelectProps) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['plan-stage-subs', planId],
    queryFn: () => plansSvc.getPlanStageSubs(planId),
    enabled: Number.isFinite(planId) && planId > 0,
    staleTime: 30_000,
  });

  const mutation = useMutation({
    mutationFn: (stageSubId: number) => plansSvc.updatePlan(planId, { stageSubId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan-stage-subs', planId] });
      queryClient.invalidateQueries({ queryKey: ['plan-progress-bar', planId] });
      if (patientId !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['patient-plans', patientId] });
      }
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error('Failed to update plan status', err);
    },
  });

  const options = [
    { value: 0, label: 'None' },
    ...((data?.options ?? []).map((opt) => ({
      value: opt.stageSubId,
      label: opt.name,
    }))),
  ];

  // Hide entirely when the plan's stage has no sub-statuses defined (legacy
  // still renders the select, but with only the None option — keep that
  // behavior so users can clear an inherited sub-status).

  return (
    <Select<number>
      label={hideLabel ? undefined : 'Status'}
      options={options}
      value={data?.currentStageSubId ?? 0}
      onChange={(next) => {
        if (next === null || next === undefined) return;
        if (next === (data?.currentStageSubId ?? 0)) return;
        mutation.mutate(next);
      }}
      disabled={isLoading || mutation.isPending}
      loading={isLoading || mutation.isPending}
    />
  );
}

export default PlanStatusSelect;
